import { ALL_FORMATS, AudioSampleSink, BlobSource, Input } from "mediabunny";

type Box = { offset: number; size: number; type: string };

export type GaplessPatchResult = {
  calibrationPeakFrame: number;
  encoderDelayFrames: number;
  mediaStartFrame: number;
};

export async function patchAacEditList(
  handle: FileSystemFileHandle,
  desiredFrames: number,
  sampleRate: number,
  calibrationFrames: number,
  calibrationImpulseFrame: number,
): Promise<GaplessPatchResult> {
  const file = await handle.getFile();
  const calibrationPeakFrame = await findCalibrationPeak(
    file,
    sampleRate,
    calibrationFrames,
  );
  const encoderDelayFrames = calibrationPeakFrame - calibrationImpulseFrame;
  if (encoderDelayFrames < 0 || encoderDelayFrames > sampleRate / 5) {
    throw new Error(
      `AAC encoder delayを特定できませんでした（${encoderDelayFrames} samples）`,
    );
  }
  const mediaStartFrame = encoderDelayFrames + calibrationFrames;
  await writeEditList(handle, file, desiredFrames, sampleRate, mediaStartFrame);
  return { calibrationPeakFrame, encoderDelayFrames, mediaStartFrame };
}

async function findCalibrationPeak(
  file: File,
  sampleRate: number,
  calibrationFrames: number,
): Promise<number> {
  const input = new Input({
    formats: ALL_FORMATS,
    source: new BlobSource(file, { maxCacheSize: 1024 * 1024 }),
  });
  try {
    const track = await input.getPrimaryAudioTrack();
    if (!track) throw new Error("校正用AAC trackを再読込できません");
    const sink = new AudioSampleSink(track);
    const scanFrames = calibrationFrames + Math.round(sampleRate * 0.15);
    let sequentialFrame = 0;
    let peakFrame = -1;
    let peak = 0;
    for await (const sample of sink.samples()) {
      const plane = new Float32Array(sample.numberOfFrames);
      sample.copyTo(plane, { planeIndex: 0, format: "f32-planar" });
      for (let index = 0; index < plane.length; index += 1) {
        const amplitude = Math.abs(plane[index]);
        if (amplitude > peak) {
          peak = amplitude;
          peakFrame = sequentialFrame + index;
        }
      }
      sequentialFrame += sample.numberOfFrames;
      sample.close();
      if (sequentialFrame >= scanFrames) break;
    }
    if (peakFrame < 0 || peak < 0.2) {
      throw new Error(
        `AAC校正impulseを検出できませんでした（peak ${peak.toFixed(3)}）`,
      );
    }
    return peakFrame;
  } finally {
    input.dispose();
  }
}

async function writeEditList(
  handle: FileSystemFileHandle,
  file: File,
  desiredFrames: number,
  sampleRate: number,
  mediaStartFrame: number,
): Promise<void> {
  const initial = new Uint8Array(
    await file.slice(0, Math.min(file.size, 32 * 1024 * 1024)).arrayBuffer(),
  );
  const roots = parseBoxes(initial, 0, initial.length);
  const moov = roots.find((box) => box.type === "moov");
  const free = roots.find(
    (box) =>
      box.type === "free" && moov && box.offset === moov.offset + moov.size,
  );
  if (!moov || !free) {
    throw new Error("MP4の予約metadata領域を確認できませんでした");
  }
  // A long export's mdat box is much larger than this bounded prefix read, so
  // it deliberately does not appear in parseBoxes(). Its header immediately
  // follows the reserved fast-start free box.
  const mdatOffset = free.offset + free.size;
  if (
    mdatOffset + 8 > initial.length ||
    readType(initial, mdatOffset + 4) !== "mdat"
  ) {
    throw new Error("MP4のmedia data領域を確認できませんでした");
  }

  const moovBytes = initial.slice(moov.offset, moov.offset + moov.size);
  const moovChildren = parseBoxes(moovBytes, 8, moovBytes.length);
  const mvhd = requireBox(moovChildren, "mvhd");
  const movieTimescale = readUint32(moovBytes, mvhd.offset + 20);
  const presentationDuration = Math.round(
    (desiredFrames * movieTimescale) / sampleRate,
  );
  writeUint32(moovBytes, mvhd.offset + 24, presentationDuration);

  const tracks = moovChildren.filter((box) => box.type === "trak");
  const audioTrack = tracks.find((track) => isAudioTrack(moovBytes, track));
  if (!audioTrack) throw new Error("MP4内のAAC trackを特定できませんでした");
  const audioBytes = moovBytes.slice(
    audioTrack.offset,
    audioTrack.offset + audioTrack.size,
  );
  const audioChildren = parseBoxes(audioBytes, 8, audioBytes.length);
  const tkhd = requireBox(audioChildren, "tkhd");
  writeUint32(audioBytes, tkhd.offset + 28, presentationDuration);
  const audioTimescale = getMediaTimescale(audioBytes, audioChildren);
  const mediaStartTime = Math.round(
    (mediaStartFrame * audioTimescale) / sampleRate,
  );
  const insertionOffset = tkhd.offset + tkhd.size;
  const editList = makeEditList(presentationDuration, mediaStartTime);
  const expandedAudio = new Uint8Array(audioBytes.length + editList.length);
  expandedAudio.set(audioBytes.subarray(0, insertionOffset), 0);
  expandedAudio.set(editList, insertionOffset);
  expandedAudio.set(
    audioBytes.subarray(insertionOffset),
    insertionOffset + editList.length,
  );
  writeUint32(expandedAudio, 0, expandedAudio.length);

  const expandedMoov = new Uint8Array(moovBytes.length + editList.length);
  expandedMoov.set(moovBytes.subarray(0, audioTrack.offset), 0);
  expandedMoov.set(expandedAudio, audioTrack.offset);
  expandedMoov.set(
    moovBytes.subarray(audioTrack.offset + audioTrack.size),
    audioTrack.offset + expandedAudio.length,
  );
  writeUint32(expandedMoov, 0, expandedMoov.length);

  const nextFreeSize = free.size - editList.length;
  if (nextFreeSize < 8) throw new Error("MP4 metadata予約領域が不足しています");
  const prefix = new Uint8Array(mdatOffset);
  prefix.set(initial.subarray(0, moov.offset), 0);
  prefix.set(expandedMoov, moov.offset);
  const freeOffset = moov.offset + expandedMoov.length;
  writeUint32(prefix, freeOffset, nextFreeSize);
  writeType(prefix, freeOffset + 4, "free");
  const writer = await handle.createWritable({ keepExistingData: true });
  try {
    await writer.write({ type: "write", position: 0, data: prefix });
  } finally {
    await writer.close();
  }
}

function getMediaTimescale(audioBytes: Uint8Array, children: Box[]): number {
  const mdia = requireBox(children, "mdia");
  const mediaChildren = parseBoxes(
    audioBytes,
    mdia.offset + 8,
    mdia.offset + mdia.size,
  );
  const mdhd = requireBox(mediaChildren, "mdhd");
  const version = audioBytes[mdhd.offset + 8];
  if (version !== 0) {
    throw new Error("64-bit MP4 media headerには未対応です");
  }
  const timescale = readUint32(audioBytes, mdhd.offset + 20);
  if (timescale === 0) throw new Error("AAC trackのtimescaleが不正です");
  return timescale;
}

function isAudioTrack(moov: Uint8Array, track: Box): boolean {
  const trackChildren = parseBoxes(
    moov,
    track.offset + 8,
    track.offset + track.size,
  );
  const mdia = trackChildren.find((box) => box.type === "mdia");
  if (!mdia) return false;
  const mediaChildren = parseBoxes(
    moov,
    mdia.offset + 8,
    mdia.offset + mdia.size,
  );
  const hdlr = mediaChildren.find((box) => box.type === "hdlr");
  return hdlr ? readType(moov, hdlr.offset + 16) === "soun" : false;
}

function makeEditList(
  segmentDuration: number,
  mediaStartFrame: number,
): Uint8Array {
  const edts = new Uint8Array(36);
  writeUint32(edts, 0, 36);
  writeType(edts, 4, "edts");
  writeUint32(edts, 8, 28);
  writeType(edts, 12, "elst");
  writeUint32(edts, 16, 0); // version + flags
  writeUint32(edts, 20, 1); // entry count
  writeUint32(edts, 24, segmentDuration);
  writeUint32(edts, 28, mediaStartFrame);
  edts[32] = 0;
  edts[33] = 1; // media rate integer
  edts[34] = 0;
  edts[35] = 0;
  return edts;
}

function parseBoxes(bytes: Uint8Array, start: number, end: number): Box[] {
  const boxes: Box[] = [];
  let offset = start;
  while (offset + 8 <= end) {
    const size = readUint32(bytes, offset);
    const type = readType(bytes, offset + 4);
    if (size < 8 || offset + size > end) break;
    boxes.push({ offset, size, type });
    offset += size;
  }
  return boxes;
}

function requireBox(boxes: Box[], type: string): Box {
  const box = boxes.find((candidate) => candidate.type === type);
  if (!box) throw new Error(`MP4 ${type} boxが見つかりません`);
  return box;
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return new DataView(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength,
  ).getUint32(offset);
}

function writeUint32(bytes: Uint8Array, offset: number, value: number): void {
  new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).setUint32(
    offset,
    value,
  );
}

function readType(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(
    bytes[offset],
    bytes[offset + 1],
    bytes[offset + 2],
    bytes[offset + 3],
  );
}

function writeType(bytes: Uint8Array, offset: number, type: string): void {
  for (let index = 0; index < 4; index += 1)
    bytes[offset + index] = type.charCodeAt(index);
}
